import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, NonIdealState, Spinner, Card, Icon, InputGroup, Tag, Collapse, Dialog, FormGroup, Classes, Intent, TextArea, MenuItem, Callout } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import { MultiSelect, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { Node } from '@xyflow/react';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import CollaborativeMindmap from '../Mindmap/CollaborativeMindmap';
import DraggableDivider from '../chat/DraggableDivider';
import InlineEditableText from '../common/InlineEditableText';
import NodeDetailSidebar from '../Mindmap/NodeDetailSidebar';
import Comments from '../comments/Comments';
import RepositoryLinker from '../Mindmap/NodeTypes/RepositoryLinker';
import StatusSelector from '../Status/StatusSelector';
import LeadSelector from '../Lead/LeadSelector';
import './MindmapInterface.css';
import MindmapSidebarDnD from '../FolderTree/MindmapSidebarDnD';
import MindmapSettingsPopover from '../Mindmap/MindmapSettingsPopover';

interface Mindmap {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  nodeCount?: number;
  folderId?: string;
}

const MindmapInterface: React.FC = observer(() => {
  const appStore = useAppStore();
  const { organization: organizationStore, user: userStore, preferences } = appStore;
  const { mindmapId } = useParams<{ mindmapId?: string }>();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(preferences.isMindmapSidebarCollapsed);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderType, setNewFolderType] = useState<'private' | 'shared'>('private');
  const [draggedItem, setDraggedItem] = useState<{ id: string; type: 'mindmap' | 'folder' } | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  
  // Get theme for styling
  const { theme: themeStore } = appStore;
  const isDark = themeStore.isDark;
  
  // Right sidebar (node details) state - use store for collapsed state
  const nodeDetailSidebarCollapsed = appStore.mindmap.nodeDetailSidebarCollapsed;
  const [nodeDetailSidebarWidth, setNodeDetailSidebarWidth] = useState(480);
  const selectedNodeId = appStore.mindmap.selectedNodeId;
  const selectedNode = selectedNodeId ? appStore.mindmap.nodes.find(n => n.id === selectedNodeId) : null;
  
  // Modal states for collapsed sidebar actions
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showRepoModal, setShowRepoModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Link dialog state
  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  
  // Users for team member selection
  const [allUsers, setAllUsers] = useState<any[]>([]);
  
  // Load users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await organizationStore.loadOrganizationMembers();
        setAllUsers(users || []);
      } catch (error) {
        console.error('Failed to load users:', error);
        setAllUsers([]);
      }
    };
    loadUsers();
  }, [organizationStore]);

  // Save selected mindmap to preferences when it changes
  useEffect(() => {
    if (mindmapId) {
      preferences.setSelectedMindmapId(mindmapId);
    }
  }, [mindmapId, preferences]);

  // Navigate to saved mindmap on initial load if no mindmap is selected
  useEffect(() => {
    // Only run if:
    // 1. No mindmap is currently selected in URL
    // 2. App is initialized (auth + data loaded)
    // 3. We have mindmaps available
    // 4. Not currently loading
    if (!mindmapId && appStore.isInitialized && !organizationStore.isLoading && organizationStore.mindmaps.length > 0) {
      const orgSlug = userStore.currentOrganization?.slug;
      if (!orgSlug) return;
      
      let targetMindmapId: string | null = null;
      
      // First, try to load the saved mindmap preference
      if (preferences.selectedMindmapId) {
        const savedMindmap = organizationStore.mindmaps.find(m => m.id === preferences.selectedMindmapId);
        if (savedMindmap) {
          targetMindmapId = savedMindmap.id;
          console.log('[MindmapInterface] Found saved mindmap preference:', targetMindmapId);
        } else {
          console.log('[MindmapInterface] Saved mindmap no longer exists:', preferences.selectedMindmapId);
        }
      }
      
      // Fallback: Load the most recently updated mindmap
      if (!targetMindmapId && organizationStore.mindmaps.length > 0) {
        const sortedMindmaps = organizationStore.mindmaps.slice().sort((a, b) => {
          const aTime = new Date(b.updatedAt).getTime();
          const bTime = new Date(a.updatedAt).getTime();
          return aTime - bTime;
        });
        targetMindmapId = sortedMindmaps[0].id;
        console.log('[MindmapInterface] No saved preference, loading most recent mindmap:', targetMindmapId);
      }
      
      if (targetMindmapId) {
        console.log('[MindmapInterface] Navigating to mindmap:', targetMindmapId);
        navigate(`/${orgSlug}/mindmap/${targetMindmapId}`);
      }
    }
  }, [mindmapId, preferences.selectedMindmapId, organizationStore.mindmaps.length, organizationStore.isLoading, appStore.isInitialized, navigate, userStore.currentOrganization?.slug, organizationStore.mindmaps]);

  useEffect(() => {
    // Check if data is loaded, if not, attempt to load it
    const loadDataIfNeeded = async () => {
      console.log('[MindmapInterface] Checking if data needs to be loaded...');
      console.log('[MindmapInterface] State:', {
        isInitialized: appStore.isInitialized,
        hasOrgId: !!organizationStore.organizationId,
        mindmapsCount: organizationStore.mindmaps.length,
        isLoading: organizationStore.isLoading,
      });

      // If app isn't initialized and we have auth, try to initialize
      if (!appStore.isInitialized && appStore.isAuthenticated) {
        console.log('[MindmapInterface] App not initialized, attempting initialization...');
        const orgSlug = userStore.currentOrganization?.slug;
        if (orgSlug && appStore.initializeApp) {
          try {
            await appStore.initializeApp(orgSlug);
            console.log('[MindmapInterface] ✅ App initialized successfully');
          } catch (error) {
            console.error('[MindmapInterface] ❌ Failed to initialize app:', error);
          }
        }
      }

      // If we have an org but no mindmaps, load them (ONCE)
      if (organizationStore.organizationId && 
          organizationStore.mindmaps.length === 0 && 
          !organizationStore.isLoading &&
          !organizationStore.error) { // Don't retry if previous load failed
        console.log('[MindmapInterface] No mindmaps in store, loading...');
        try {
          await organizationStore.loadMindmaps();
          console.log('[MindmapInterface] ✅ Mindmaps loaded successfully');
        } catch (error) {
          console.error('[MindmapInterface] ❌ Failed to load mindmaps:', error);
          // Don't retry - error is now set in store
        }
      }

      setIsInitialized(true);
    };

    loadDataIfNeeded();
  }, [
    appStore.isInitialized,
    appStore.isAuthenticated,
    organizationStore.organizationId,
    organizationStore.mindmaps.length,
    organizationStore.isLoading,
  ]);

  const handleNewMindmap = async () => {
    try {
      const newMindmap = await organizationStore.createMindmap(
        `New Mindmap ${new Date().toLocaleDateString()}`,
        'Created from mindmap interface'
      );
      
      if (newMindmap && newMindmap.id) {
        const orgSlug = userStore.currentOrganization?.slug;
        if (!orgSlug) {
          console.error('No organization slug available, cannot navigate');
          return;
        }
        navigate(`/${orgSlug}/mindmap/${newMindmap.id}`);
      }
    } catch (error) {
      console.error('Failed to create mindmap:', error);
    }
  };

  const handleMindmapSelect = (mindmapId: string) => {
    const orgSlug = userStore.currentOrganization?.slug;
    if (!orgSlug) {
      console.error('No organization slug available, cannot navigate');
      return;
    }
    // Close node detail sidebar when switching mindmaps
    appStore.mindmap.setNodeDetailSidebarCollapsed(true);
    appStore.mindmap.selectNode(null);
    navigate(`/${orgSlug}/mindmap/${mindmapId}`);
  };
  
  // Handle node double-click - DISABLED (use expand button instead)
  const handleNodeDoubleClick = useCallback((node: Node) => {
    // Double-click disabled - use expand button on node
    console.log('Double-click disabled - use expand button');
  }, []);
  
  // Handle node expand button click - open sidebar with selected node
  const handleNodeExpand = useCallback((nodeId: string) => {
    console.log('🔍 Expand button clicked for node:', nodeId);
    // Node already selected in store by the expand button
    appStore.mindmap.setNodeDetailSidebarCollapsed(false);
    console.log('✅ Sidebar opened for node:', nodeId);
  }, [appStore.mindmap]);
  
  // Handle closing node detail sidebar
  const handleCloseNodeDetailSidebar = useCallback(() => {
    appStore.mindmap.setNodeDetailSidebarCollapsed(true);
    appStore.mindmap.selectNode(null);
  }, [appStore.mindmap]);
  
  // Handle node updates from sidebar
  const handleUpdateNode = useCallback((nodeId: string, data: any) => {
    // This would connect to your backend API
    console.log('Update node:', nodeId, data);
  }, []);
  
  // Handle node deletion from sidebar
  const handleDeleteNode = useCallback((nodeId: string) => {
    // This would connect to your backend API
    console.log('Delete node:', nodeId);
    setNodeDetailSidebarCollapsed(true);
    setSelectedNode(null);
  }, []);

  const handleDeleteMindmap = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this mindmap?')) {
      try {
        await organizationStore.deleteMindmap(id);
        // If we're viewing the deleted mindmap, navigate to dashboard
        if (mindmapId === id) {
          const orgSlug = userStore.currentOrganization?.slug;
          if (!orgSlug) {
            console.error('No organization slug available, redirecting to login');
            navigate('/login');
            return;
          }
          navigate(`/${orgSlug}/dashboard`);
        }
      } catch (error) {
        console.error('Failed to delete mindmap:', error);
      }
    }
  };

  const handleRenameMindmap = async (id: string, newName: string) => {
    try {
      await organizationStore.renameMindmap(id, newName);
    } catch (error) {
      console.error('Failed to rename mindmap:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await organizationStore.createFolder(newFolderName.trim(), newFolderType);
      setShowNewFolderDialog(false);
      setNewFolderName('');
      setNewFolderType('private');
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };
  
  // Helper functions for modals
  const handleAddLink = async () => {
    if (!selectedNode?.id) return;
    
    if (!newLinkName.trim()) {
      setLinkError('Link name is required');
      return;
    }
    
    if (!newLinkUrl.trim()) {
      setLinkError('Link URL is required');
      return;
    }
    
    try {
      new URL(newLinkUrl);
    } catch {
      setLinkError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    
    const newLink = { name: newLinkName.trim(), url: newLinkUrl.trim() };
    const updatedLinks = [...(selectedNode.links || []), newLink];
    
    appStore.mindmap.updateNodeOptimistically(selectedNode.id, { links: updatedLinks });
    
    setNewLinkName('');
    setNewLinkUrl('');
    setLinkError(null);
    setShowAddLinkDialog(false);
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (window.confirm('Are you sure you want to delete this folder? Mindmaps inside will be moved to root.')) {
      try {
        await organizationStore.deleteFolder(folderId);
      } catch (error) {
        console.error('Failed to delete folder:', error);
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, item: { id: string; type: 'mindmap' | 'folder' }) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, targetId?: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(targetId || 'root');
  };

  const handleDrop = async (e: React.DragEvent, targetType: 'folder' | 'shared' | 'private', targetId?: string) => {
    e.preventDefault();
    setDragOverItem(null);
    
    if (!draggedItem) return;
    
    if (draggedItem.type === 'mindmap') {
      // Handle mindmap drop
      const mindmap = organizationStore.mindmaps.find(m => m.id === draggedItem.id);
      if (!mindmap) return;
      
      if (targetType === 'folder') {
        // Move to folder
        await organizationStore.moveMindmapToFolder(draggedItem.id, targetId || null);
      } else if (targetType === 'shared' || targetType === 'private') {
        // Change visibility
        const newVisibility = targetType;
        await organizationStore.setMindmapVisibility(draggedItem.id, newVisibility);
        if (targetId) {
          // Also move to folder if dropped on a folder in that section
          await organizationStore.moveMindmapToFolder(draggedItem.id, targetId);
        } else {
          // Move to root of that section
          await organizationStore.moveMindmapToFolder(draggedItem.id, null);
        }
      }
    } else if (draggedItem.type === 'folder') {
      // Handle folder reordering/reparenting (future enhancement)
      console.log('Folder drag not yet implemented');
    }
    
    setDraggedItem(null);
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Never';
      
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      
      if (days > 7) {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
      if (days > 0) return `${days}d ago`;
      if (hours > 0) return `${hours}h ago`;
      if (minutes > 0) return `${minutes}m ago`;
      return 'Just now';
    } catch (error) {
      return 'Never';
    }
  };

  const renderMindmapCard = (mindmap: Mindmap) => (
    <Card
      key={mindmap.id}
      className={`mindmap-card ${mindmapId === mindmap.id ? 'active' : ''} ${dragOverItem === mindmap.id ? 'drag-over' : ''}`}
      onClick={() => handleMindmapSelect(mindmap.id)}
      interactive
      elevation={mindmapId === mindmap.id ? 2 : 0}
      draggable
      onDragStart={(e) => handleDragStart(e, { id: mindmap.id, type: 'mindmap' })}
      onDragOver={(e) => handleDragOver(e, mindmap.id)}
      onDrop={(e) => e.stopPropagation()}
    >
      <div className="mindmap-card-content">
        <div className="mindmap-card-header">
          <Icon icon={IconNames.DIAGRAM_TREE} size={14} className="mindmap-icon" />
          <InlineEditableText
            value={mindmap.name}
            onSave={(newName) => handleRenameMindmap(mindmap.id, newName)}
            placeholder="Untitled Mindmap"
            className="mindmap-title"
          />
          {mindmapId === mindmap.id && (
            <Icon icon={IconNames.DOT} className="active-indicator" size={10} />
          )}
        </div>
        
        {mindmap.description && (
          <p className="mindmap-description">{mindmap.description}</p>
        )}
        
        <div className="mindmap-meta">
          <span className="mindmap-date">
            {formatDate(mindmap.updatedAt)}
          </span>
          {mindmap.nodeCount !== undefined && (
            <Tag minimal className="mindmap-nodes">
              {mindmap.nodeCount} nodes
            </Tag>
          )}
        </div>
      </div>
      
      <div className="mindmap-actions">
        <Popover2
          content={
            <div className="mindmap-action-menu">
              <Button
                icon={IconNames.DUPLICATE}
                text="Duplicate"
                minimal
                onClick={(e) => {
                  e.stopPropagation();
                  organizationStore.duplicateMindmap(mindmap.id);
                }}
              />
              <Button
                icon={IconNames.EXPORT}
                text="Export"
                minimal
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Implement export
                }}
              />
              <Button
                icon={IconNames.TRASH}
                text="Delete"
                minimal
                intent="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMindmap(mindmap.id);
                }}
              />
            </div>
          }
          placement="bottom-start"
          minimal
        >
          <Button
            icon={IconNames.MORE}
            minimal
            small
            onClick={(e) => e.stopPropagation()}
          />
        </Popover2>
      </div>
    </Card>
  );

  const filteredMindmaps = organizationStore.mindmaps.filter(mindmap =>
    !searchQuery || mindmap.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mindmap.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sharedMindmaps = filteredMindmaps.filter((m: any) => m.visibility === 'shared' && !m.folderId);
  const privateMindmaps = filteredMindmaps.filter((m: any) => m.visibility !== 'shared' && !m.folderId);
  const sharedFolders = organizationStore.folders.filter((f: any) => f.type === 'shared');
  const privateFolders = organizationStore.folders.filter((f: any) => f.type === 'private');
  
  const renderFolder = (folder: any, isShared: boolean) => {
    const mindmapsInFolder = filteredMindmaps.filter((m: any) => m.folderId === folder.id);
    const isExpanded = expandedFolders.has(folder.id);
    
    return (
      <div key={folder.id} className="folder-container">
        <div
          className={`folder-header ${dragOverItem === folder.id ? 'drag-over' : ''}`}
          draggable
          onDragStart={(e) => handleDragStart(e, { id: folder.id, type: 'folder' })}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDrop={(e) => handleDrop(e, 'folder', folder.id)}
        >
          <Button
            icon={isExpanded ? IconNames.FOLDER_OPEN : IconNames.FOLDER_CLOSE}
            minimal
            small
            onClick={() => toggleFolder(folder.id)}
          />
          <InlineEditableText
            value={folder.name}
            onSave={(newName) => organizationStore.renameFolder(folder.id, newName)}
            placeholder="Untitled Folder"
            className="folder-name"
          />
          <Popover2
            content={
              <div className="folder-action-menu">
                <Button
                  icon={IconNames.TRASH}
                  text="Delete"
                  minimal
                  intent="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder.id);
                  }}
                />
              </div>
            }
            placement="bottom-start"
            minimal
          >
            <Button
              icon={IconNames.MORE}
              minimal
              small
              onClick={(e) => e.stopPropagation()}
            />
          </Popover2>
        </div>
        <Collapse isOpen={isExpanded}>
          <div className="folder-contents">
            {mindmapsInFolder.length === 0 ? (
              <div className="empty-folder">Empty folder</div>
            ) : (
              mindmapsInFolder.map(renderMindmapCard)
            )}
          </div>
        </Collapse>
      </div>
    );
  };

  if (!isInitialized) {
    return (
      <div className="mindmap-loading">
        <Spinner size={50} />
        <p>Loading mindmaps...</p>
      </div>
    );
  }

  const currentMindmap = mindmapId ? 
    organizationStore.mindmaps.find(m => m.id === mindmapId) : null;

  return (
    <div className="mindmap-interface">
      {/* Mindmap List Sidebar */}
      <div 
        className={`mindmap-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
        style={sidebarCollapsed ? { width: 80 } : { width: sidebarWidth }}
      >
        {/* Collapsed state - show vertical icon buttons */}
        {sidebarCollapsed && (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            paddingTop: '12px',
            paddingBottom: '12px',
            backgroundColor: isDark ? '#252A31' : '#ffffff',
            borderRight: isDark ? '1px solid #383E47' : '1px solid #e5e7eb',
            gap: '4px',
            overflow: 'auto'
          }}>
            {/* Expand button */}
            <Button
              icon={IconNames.CHEVRON_RIGHT}
              minimal
              onClick={() => {
                setSidebarCollapsed(false);
                preferences.setMindmapSidebarCollapsed(false);
              }}
              title="Expand sidebar"
              style={{ marginBottom: '12px' }}
            />
            
            {/* Active mindmap indicator */}
            {currentMindmap && (
              <Button
                icon={IconNames.DIAGRAM_TREE}
                minimal
                active={true}
                onClick={() => {
                  setSidebarCollapsed(false);
                  preferences.setMindmapSidebarCollapsed(false);
                }}
                title={currentMindmap.name}
                style={{ 
                  backgroundColor: isDark ? 'rgba(138, 155, 168, 0.15)' : 'rgba(92, 112, 128, 0.1)',
                  color: isDark ? '#48AFF0' : '#2B95D6'
                }}
              />
            )}
            
            {/* Quick mindmap buttons for recent mindmaps */}
            {organizationStore.mindmaps.slice(0, 5).filter(m => m.id !== mindmapId).map((mindmap) => (
              <Button
                key={mindmap.id}
                icon={IconNames.DIAGRAM_TREE}
                minimal
                onClick={() => handleMindmapSelect(mindmap.id)}
                title={mindmap.name}
                style={{ opacity: 0.6 }}
              />
            ))}
            
            <div style={{ flex: 1 }} />
            
            {/* Add new mindmap button */}
            <Button
              icon={IconNames.PLUS}
              minimal
              intent="primary"
              onClick={handleNewMindmap}
              title="Create new mindmap"
            />
            
            {/* Folders button */}
            <Button
              icon={IconNames.FOLDER_NEW}
              minimal
              onClick={() => setShowNewFolderDialog(true)}
              title="Create new folder"
            />
          </div>
        )}
        {/* Mindmap sidebar with drag and drop - MindmapSidebarDnD has its own header and search */}
        {!sidebarCollapsed && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <MindmapSidebarDnD
              onSelect={(id: string) => handleMindmapSelect(id)}
              activeMindmapId={mindmapId}
              onToggleCollapse={() => {
                setSidebarCollapsed(true);
                preferences.setMindmapSidebarCollapsed(true);
              }}
            />
          </div>
        )}
      </div>

      {/* Draggable Divider */}
      {!sidebarCollapsed && (
        <DraggableDivider
          onResize={(delta) => {
            const newWidth = Math.max(200, Math.min(600, sidebarWidth + delta));
            setSidebarWidth(newWidth);
          }}
          orientation="vertical"
        />
      )}

      {/* Main Mindmap Area */}
      <div className="mindmap-main">
        {mindmapId ? (
          <>
            {/* Mindmap Header */}
            <div className="mindmap-header">
              <div className="mindmap-header-left">
                <InlineEditableText
                  value={currentMindmap?.name || 'Untitled Mindmap'}
                  onSave={async (newName) => {
                    if (mindmapId && newName.trim()) {
                      await handleRenameMindmap(mindmapId, newName.trim());
                    }
                  }}
                  className="mindmap-title-header"
                  placeholder="Untitled Mindmap"
                  maxLength={200}
                />
                {currentMindmap?.nodeCount !== undefined && (
                  <Tag minimal className="mindmap-node-count">
                    {currentMindmap.nodeCount} nodes
                  </Tag>
                )}
              </div>
              <div className="mindmap-header-right">
                <Popover2
                  content={
                    <div className="mindmap-action-menu">
                      <Button
                        icon={IconNames.TRASH}
                        text="Delete Mindmap"
                        minimal
                        intent="danger"
                        onClick={async () => {
                          if (confirm('Are you sure you want to delete this mindmap?')) {
                            await organizationStore.deleteMindmap(mindmapId);
                            // Navigate to org-based mindmap list route
                            const orgSlug = userStore.currentOrganization?.slug;
                            if (orgSlug) {
                              navigate(`/${orgSlug}/mindmap`);
                            } else {
                              navigate('/login');
                            }
                          }
                        }}
                      />
                    </div>
                  }
                  placement="bottom-end"
                  minimal
                >
                  <Button
                    icon={IconNames.MORE}
                    minimal
                    title="More Options"
                  />
                </Popover2>
              </div>
            </div>

            {/* Mindmap Content */}
            <div className="mindmap-content">
              <CollaborativeMindmap 
                mindmapId={mindmapId}
                onNodeDoubleClick={handleNodeDoubleClick}
                onNodeExpand={handleNodeExpand}
                selectedNodeId={selectedNode?.id || null}
              />
              {/* Mindmap Settings Popover - positioned in top-right */}
              <MindmapSettingsPopover />
            </div>
          </>
        ) : (
          <NonIdealState
            icon={IconNames.DIAGRAM_TREE}
            title="No Mindmap Selected"
            description="Select a mindmap from the list or create a new one to start mapping your ideas."
            action={
              <Button
                icon={IconNames.PLUS}
                intent="primary"
                onClick={handleNewMindmap}
              >
                New Mindmap
              </Button>
            }
          />
        )}
      </div>

      {/* Right Draggable Divider for Node Detail Sidebar */}
      {!nodeDetailSidebarCollapsed && mindmapId && (
        <DraggableDivider
          onResize={(delta) => {
            const newWidth = Math.max(300, Math.min(800, nodeDetailSidebarWidth - delta));
            setNodeDetailSidebarWidth(newWidth);
          }}
          orientation="vertical"
        />
      )}

      {/* Node Detail Sidebar (Right) */}
      {mindmapId && (
        <div 
          className={`mindmap-sidebar mindmap-sidebar-right ${nodeDetailSidebarCollapsed ? 'collapsed' : ''}`}
          style={nodeDetailSidebarCollapsed ? { width: 60, height: '100%' } : { width: nodeDetailSidebarWidth, height: '100%' }}
        >
          {/* Collapsed state - show vertical action buttons */}
          {nodeDetailSidebarCollapsed && (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              paddingTop: '12px',
              paddingBottom: '12px',
              backgroundColor: isDark ? '#252A31' : '#ffffff',
              borderLeft: isDark ? '1px solid #383E47' : '1px solid #e5e7eb',
              gap: '4px'
            }}>
              {/* Expand button */}
            <Button
              icon={IconNames.CHEVRON_LEFT}
              minimal
              onClick={() => appStore.mindmap.setNodeDetailSidebarCollapsed(false)}
                title="Expand node details"
                style={{ marginBottom: '12px' }}
              />
              
              {/* Node indicator - show if node is selected */}
              {selectedNode && (
                <>
                  <Button
                    icon={IconNames.BOX}
                    minimal
                    active={true}
                    onClick={() => appStore.mindmap.setNodeDetailSidebarCollapsed(false)}
                    title={`Node: ${selectedNode.label || 'Untitled'}`}
                    style={{ 
                      backgroundColor: isDark ? 'rgba(138, 155, 168, 0.15)' : 'rgba(92, 112, 128, 0.1)',
                      color: '#E91E63'
                    }}
                  />
                  
                  <div style={{ 
                    width: '100%', 
                    height: '1px', 
                    backgroundColor: isDark ? '#383E47' : '#e5e7eb',
                    margin: '8px 0'
                  }} />
                  
                  {/* Quick action buttons */}
                  <Button
                    icon={IconNames.COMMENT}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Open comments modal instead of expanding sidebar
                      setShowCommentsModal(true);
                    }}
                    title="View comments"
                  />
                  
                  <Button
                    icon={IconNames.GIT_REPO}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Open repository modal
                      setShowRepoModal(true);
                    }}
                    title="Link repository"
                  />
                  
                  <Button
                    icon={IconNames.USER}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Open team members modal
                      setShowMembersModal(true);
                    }}
                    title="Assign members"
                  />
                  
                  <Button
                    icon={IconNames.LINK}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Open links modal
                      setShowLinksModal(true);
                    }}
                    title="Add link"
                  />
                  
                  <Button
                    icon={IconNames.TAG}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Open status modal
                      setShowStatusModal(true);
                    }}
                    title="Change status"
                  />
                  
                  <div style={{ flex: 1 }} />
                  
                  <Button
                    icon={IconNames.DOCUMENT_OPEN}
                    minimal
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Navigate to node document - don't open sidebar
                      if (selectedNode?.id) {
                        navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${selectedNode.id}`);
                      }
                    }}
                    title="Open node document"
                  />
                  
                  <Button
                    icon={IconNames.TRASH}
                    minimal
                    intent="danger"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Delete action - don't open sidebar
                      if (selectedNode?.id) {
                        if (confirm(`Delete node "${selectedNode.label || 'Untitled'}"?`)) {
                          if (onDeleteNode) {
                            onDeleteNode(selectedNode.id);
                          }
                        }
                      }
                    }}
                    title="Delete node"
                  />
                </>
              )}
              
              {/* No node selected state */}
              {!selectedNode && (
                <div style={{ 
                  padding: '8px', 
                  textAlign: 'center',
                  color: isDark ? '#8A9BA8' : '#738694',
                  fontSize: '11px',
                  marginTop: '20px'
                }}>
                  <Icon icon={IconNames.SELECTION} size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                    Select a node
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Node Detail Sidebar content */}
          {!nodeDetailSidebarCollapsed && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <NodeDetailSidebar
                selectedNode={selectedNode}
                isOpen={!nodeDetailSidebarCollapsed}
                onClose={handleCloseNodeDetailSidebar}
                onUpdateNode={handleUpdateNode}
                onDeleteNode={handleDeleteNode}
              />
            </div>
          )}
        </div>
      )}
      
      {/* New Folder Dialog */}
      <Dialog
        isOpen={showNewFolderDialog}
        onClose={() => setShowNewFolderDialog(false)}
        title="Create New Folder"
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="Folder Name" labelInfo="(required)">
            <InputGroup
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Folder Type">
            <div className="bp5-button-group">
              <Button
                text="Private"
                onClick={() => setNewFolderType('private')}
                intent={newFolderType === 'private' ? Intent.PRIMARY : Intent.NONE}
              />
              <Button
                text="Shared"
                onClick={() => setNewFolderType('shared')}
                intent={newFolderType === 'shared' ? Intent.PRIMARY : Intent.NONE}
              />
            </div>
          </FormGroup>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowNewFolderDialog(false)}>Cancel</Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
            >
              Create Folder
            </Button>
          </div>
        </div>
      </Dialog>
      
      {/* Quick Action Modals from Collapsed Sidebar */}
      {/* Comments Modal */}
      <Dialog
        isOpen={showCommentsModal}
        onClose={() => setShowCommentsModal(false)}
        title={`Comments - ${selectedNode?.label || 'Node'}`}
        style={{ width: '600px', maxHeight: '80vh' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedNode && <Comments nodeId={selectedNode.id} />}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowCommentsModal(false)}>Close</Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={() => {
                setShowCommentsModal(false);
                appStore.mindmap.setNodeDetailSidebarCollapsed(false);
              }}
            >
              Open Full Details
            </Button>
          </div>
        </div>
      </Dialog>
      
      {/* Repository Linker Modal */}
      <Dialog
        isOpen={showRepoModal}
        onClose={() => setShowRepoModal(false)}
        title={`Link Repository - ${selectedNode?.label || 'Node'}`}
        style={{ width: '700px', maxHeight: '80vh' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedNode && mindmapId && (
            <RepositoryLinker
              nodeId={selectedNode.id}
              mindmapId={mindmapId}
              linkedRepositories={appStore.mindmap.getNodeGithubLinks(selectedNode.id).map((link: any) => ({
                id: link.metadata?.id || 0,
                name: link.repositoryName,
                full_name: link.repositoryFullName,
                html_url: link.repositoryUrl,
                description: link.metadata?.description
              }))}
              onRepositoriesChange={() => {
                // Observer pattern will handle update
                console.log('Repositories updated');
              }}
              autoOpenDialog={true}
            />
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowRepoModal(false)}>Close</Button>
          </div>
        </div>
      </Dialog>
      
      {/* Team Members Modal */}
      <Dialog
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        title={`Assign Team Members - ${selectedNode?.label || 'Node'}`}
        style={{ width: '600px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedNode && (
            <>
              <FormGroup label="Support Members" helperText="Select one or more team members to support this node">
                <MultiSelect<any>
                  items={allUsers}
                  selectedItems={allUsers.filter(u => (selectedNode.supportMembers || []).includes(u.id))}
                  onItemSelect={(user) => {
                    const currentMembers = selectedNode.supportMembers || [];
                    const newMembers = currentMembers.includes(user.id)
                      ? currentMembers.filter(id => id !== user.id)
                      : [...currentMembers, user.id];
                    appStore.mindmap.updateNodeOptimistically(selectedNode.id, { supportMembers: newMembers });
                  }}
                  itemRenderer={(user, { handleClick, modifiers }) => (
                    <MenuItem
                      key={user.id}
                      text={user.name || user.email}
                      label={user.email}
                      icon={modifiers.active || (selectedNode.supportMembers || []).includes(user.id) ? 'tick' : 'blank'}
                      onClick={handleClick}
                      active={modifiers.active}
                      shouldDismissPopover={false}
                    />
                  )}
                  itemPredicate={(query: string, user: any) => {
                    const normalizedQuery = query.toLowerCase();
                    return (user.name || '').toLowerCase().includes(normalizedQuery) ||
                           (user.email || '').toLowerCase().includes(normalizedQuery);
                  }}
                  tagRenderer={(user) => user.name || user.email}
                  tagInputProps={{
                    onRemove: (value, index) => {
                      const currentMembers = selectedNode.supportMembers || [];
                      const userId = allUsers.find(u => (u.name || u.email) === value)?.id;
                      if (userId) {
                        const newMembers = currentMembers.filter(id => id !== userId);
                        appStore.mindmap.updateNodeOptimistically(selectedNode.id, { supportMembers: newMembers });
                      }
                    },
                  }}
                  placeholder="Select support members..."
                  fill
                  popoverProps={{ minimal: true }}
                />
              </FormGroup>
              <FormGroup label="Followers" helperText="Select one or more users to follow this node">
                <MultiSelect<any>
                  items={allUsers}
                  selectedItems={allUsers.filter(u => (selectedNode.followers || []).includes(u.id))}
                  onItemSelect={(user) => {
                    const currentFollowers = selectedNode.followers || [];
                    const newFollowers = currentFollowers.includes(user.id)
                      ? currentFollowers.filter(id => id !== user.id)
                      : [...currentFollowers, user.id];
                    appStore.mindmap.updateNodeOptimistically(selectedNode.id, { followers: newFollowers });
                  }}
                  itemRenderer={(user, { handleClick, modifiers }) => (
                    <MenuItem
                      key={user.id}
                      text={user.name || user.email}
                      label={user.email}
                      icon={modifiers.active || (selectedNode.followers || []).includes(user.id) ? 'tick' : 'blank'}
                      onClick={handleClick}
                      active={modifiers.active}
                      shouldDismissPopover={false}
                    />
                  )}
                  itemPredicate={(query: string, user: any) => {
                    const normalizedQuery = query.toLowerCase();
                    return (user.name || '').toLowerCase().includes(normalizedQuery) ||
                           (user.email || '').toLowerCase().includes(normalizedQuery);
                  }}
                  tagRenderer={(user) => user.name || user.email}
                  tagInputProps={{
                    onRemove: (value, index) => {
                      const currentFollowers = selectedNode.followers || [];
                      const userId = allUsers.find(u => (u.name || u.email) === value)?.id;
                      if (userId) {
                        const newFollowers = currentFollowers.filter(id => id !== userId);
                        appStore.mindmap.updateNodeOptimistically(selectedNode.id, { followers: newFollowers });
                      }
                    },
                  }}
                  placeholder="Select followers..."
                  fill
                  popoverProps={{ minimal: true }}
                />
              </FormGroup>
            </>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowMembersModal(false)}>Close</Button>
          </div>
        </div>
      </Dialog>
      
      {/* Links Modal */}
      <Dialog
        isOpen={showLinksModal}
        onClose={() => setShowLinksModal(false)}
        title={`Links - ${selectedNode?.label || 'Node'}`}
        style={{ width: '600px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedNode && (
            <>
              {(selectedNode.links || []).length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: 'var(--text-muted)',
                  backgroundColor: isDark ? '#16181C' : '#F5F8FA',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <Icon icon={IconNames.LINK} size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <div style={{ fontSize: '14px' }}>No links added yet</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>Click "Add Link" below to get started</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {(selectedNode.links || []).map((link: any, index: number) => (
                    <Card key={index} interactive style={{
                      padding: '12px',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <Icon icon={IconNames.LINK} size={16} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>{link.name}</div>
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            fontSize: '12px', 
                            color: isDark ? '#48AFF0' : '#2B95D6',
                            textDecoration: 'none',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {link.url}
                        </a>
                      </div>
                      <Button
                        icon={IconNames.TRASH}
                        minimal
                        small
                        intent="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedNode.id) {
                            const newLinks = (selectedNode.links || []).filter((_: any, i: number) => i !== index);
                            appStore.mindmap.updateNodeOptimistically(selectedNode.id, { links: newLinks });
                          }
                        }}
                      />
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Add link button */}
              <Button
                icon={IconNames.PLUS}
                text="Add Link"
                intent={Intent.PRIMARY}
                onClick={() => {
                  setLinkError(null);
                  setNewLinkName('');
                  setNewLinkUrl('');
                  setShowAddLinkDialog(true);
                }}
                fill
              />
            </>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowLinksModal(false)}>Close</Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={() => {
                setShowLinksModal(false);
                appStore.mindmap.setNodeDetailSidebarCollapsed(false);
              }}
            >
              Open Full Details
            </Button>
          </div>
        </div>
      </Dialog>
      
      {/* Add Link Dialog (sub-dialog) */}
      <Dialog
        isOpen={showAddLinkDialog}
        onClose={() => {
          setShowAddLinkDialog(false);
          setLinkError(null);
        }}
        title="Add Link"
        style={{ width: '500px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="Link Name" labelInfo="(required)">
            <InputGroup
              value={newLinkName}
              onChange={(e) => setNewLinkName(e.target.value)}
              placeholder="e.g., Project Documentation"
              autoFocus
            />
          </FormGroup>
          <FormGroup label="URL" labelInfo="(required)" helperText="Enter a valid URL starting with http:// or https://">
            <InputGroup
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
              placeholder="https://example.com"
              leftIcon={IconNames.LINK}
            />
          </FormGroup>
          {linkError && (
            <Callout intent={Intent.DANGER} style={{ marginTop: '12px' }}>
              {linkError}
            </Callout>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => {
              setShowAddLinkDialog(false);
              setLinkError(null);
            }}>
              Cancel
            </Button>
            <Button
              intent={Intent.PRIMARY}
              onClick={handleAddLink}
              disabled={!newLinkName.trim() || !newLinkUrl.trim()}
            >
              Add Link
            </Button>
          </div>
        </div>
      </Dialog>
      
      {/* Status/Lead Modal */}
      <Dialog
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={`Status & Lead - ${selectedNode?.label || 'Node'}`}
        style={{ width: '400px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedNode && (
            <>
              <FormGroup label="Status">
                <StatusSelector
                  currentStatus={selectedNode.status}
                  onStatusSelect={(status) => {
                    if (selectedNode.id) {
                      appStore.mindmap.updateNodeOptimistically(selectedNode.id, { status });
                    }
                  }}
                  minimal={false}
                  showLabel={false}
                />
              </FormGroup>
              <FormGroup label="Lead">
                <LeadSelector
                  currentLeadId={selectedNode.lead}
                  onLeadSelect={(leadId) => {
                    if (selectedNode.id) {
                      appStore.mindmap.updateNodeOptimistically(selectedNode.id, { lead: leadId || '' });
                    }
                  }}
                  minimal={false}
                  showLabel={false}
                />
              </FormGroup>
            </>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowStatusModal(false)}>Close</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default MindmapInterface;
